import React, { useState } from 'react';
import axios from 'axios';

type Msg = {
  user_institute_id: number;
  to_email: string;
  last_event_time: string;
  subject: string;
  status: string;
  from_email: string;
  msg_id: string;
  opens_count: number;
  clicks_count: number;
  platform_type: string | null;
  tag: string; // maps to category in DynamoDB & email_activity_logs
};

const MAX_ROWS = 2000;

const UploadReport: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [skippedRows, setSkippedRows] = useState<{ user_institute_id: number; to_email: string }[]>([]);

  function unquote(s: string): string {
    let t = s.trim();
    if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) {
      t = t.slice(1, -1);
    }
    return t.replace(/""/g, '"');
  }

  function splitCSV(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        out.push(cur); cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(unquote);
  }

  function splitLines(text: string): string[] {
    return text.replace(/^﻿/, '').split(/\r\n|\n|\r/).filter(ln => ln.trim().length > 0);
  }

  // Handles both "DD-MM-YYYY HH:mm" and "DD-MM-YYYY"
  function formatToISO(dateStr: string): string {
    if (!dateStr || typeof dateStr !== 'string') return new Date().toISOString();
    const trimmed = dateStr.trim();
    const parts = trimmed.split(' ');
    const datePart = parts[0];
    const timePart = parts[1] || '00:00';

    const dmy = datePart.split('-');
    if (dmy.length !== 3) return new Date().toISOString();
    const [dd, mm, yyyy] = dmy;
    if (!/^\d{2}$/.test(dd) || !/^\d{2}$/.test(mm) || !/^\d{4}$/.test(yyyy)) return new Date().toISOString();
    if (!/^\d{2}:\d{2}$/.test(timePart)) return new Date().toISOString();
    return `${yyyy}-${mm}-${dd}T${timePart}:00Z`;
  }

  const parseAndUpload = async (text: string, inputEl: HTMLInputElement) => {
    try {
      const lines = splitLines(text);

      if (lines.length < 2) {
        setError('CSV looks empty or malformed.');
        return;
      }

      const dataRowCount = lines.length - 1;
      if (dataRowCount > MAX_ROWS) {
        setError(`File contains ${dataRowCount} rows. Maximum allowed is ${MAX_ROWS} rows per upload.`);
        return;
      }

      // Case-insensitive header parsing
      const rawHeaders = splitCSV(lines[0]).map(h => h.trim());
      const headersLower = rawHeaders.map(h => h.toLowerCase());
      const required = ['id', 'subject', 'email', 'date', 'status', 'open', 'click', 'tag', 'type'];

      for (const h of required) {
        if (!headersLower.includes(h)) {
          setError(`Missing required column: "${h}" (header matching is case-insensitive)`);
          return;
        }
      }

      const messages: Msg[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cells = splitCSV(lines[i]);

        if (cells.length !== rawHeaders.length) {
          setError(`Row ${i + 1} has ${cells.length} columns, expected ${rawHeaders.length}.`);
          return;
        }

        const row: Record<string, string> = Object.fromEntries(
          headersLower.map((h, idx) => [h, cells[idx] ?? ''])
        );

        if (!row['id'] || !row['email'] || !row['subject']) {
          setError(`Row ${i + 1}: 'Id', 'email' or 'subject' is blank. Upload aborted.`);
          return;
        }

        const uiId = parseInt(row['id'], 10);
        if (isNaN(uiId)) {
          setError(`Row ${i + 1}: 'Id' must be a valid number, got "${row['id']}".`);
          return;
        }

        messages.push({
          user_institute_id: uiId,
          to_email: row['email'],
          last_event_time: formatToISO(row['date']),
          subject: row['subject'],
          status: row['status'] || '',
          from_email: 'team@moengage.com',
          msg_id: `msg${row['id']}`,
          opens_count: parseInt(row['open'] || '0', 10) || 0,
          clicks_count: parseInt(row['click'] || '0', 10) || 0,
          platform_type: row['type'] || null,
          tag: row['tag'] || '',
        });
      }

      // All validation passed — show loader before network call
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BROADCAST_SERVER}/referral/upload-moengage-v2`,
        { messages },
        { headers: { 'Content-Type': 'application/json' } }
      );

      setLoading(false);
      setSuccess(response.data?.message || `${messages.length} records uploaded successfully.`);
      setSkippedRows(response.data?.notFoundRows || []);
      inputEl.value = '';
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || err?.message || 'Upload failed. Please try again.');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccess(null);
    setSkippedRows([]);
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only CSV files are allowed.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '');
      parseAndUpload(text, event.target);
    };
    reader.readAsText(file);
  };

  return (
    <div className="max-1140 mx-auto py-5 flex-1">
      <h2 className="font-20 mb-2">Upload MoEngage Report</h2>

      <div className="alert alert-info">
        <h5>Please Note:</h5>
        <ul>
          <li>File must be in <strong>CSV</strong> format (.csv).</li>
          <li>Maximum <strong>{MAX_ROWS.toLocaleString()}</strong> rows per upload.</li>
          <li>Column headers are <strong>case-insensitive</strong> (e.g., <code>Subject</code> or <code>subject</code> both work).</li>
          <li>Required columns: <code>Id, Subject, email, Date, Status, Open, Click, tag, type</code></li>
          <li><code>Id</code> must be a valid <strong>user_institute ID</strong> (numeric).</li>
        </ul>
        <div className="d-flex align-items-center">
          <a
            href="https://s3.ap-south-1.amazonaws.com/media.univariety.dev/sample_email_data.csv"
            className="btn btn-green"
            download=""
          >
            <span className="d-flex align-items-center">
              <i className="bi bi-cloud-arrow-down-fill font-20 me-2" /> Download Sample CSV
            </span>
          </a>
        </div>
      </div>

      <label className="form-label m-0 mb-1 font-16 font-500 w-100" htmlFor="cloud-computing">
        Email Attachment (CSV only)
      </label>

      <div className="upload_section mb-2">
        <input
          type="file"
          id="cloud-computing"
          className="cursor-pointer"
          accept=".csv"
          onChange={handleFileChange}
          disabled={loading}
        />
        <div className="row g-2 align-items-center justify-content-center">
          <div className="col-sm d-flex align-items-center justify-content-center">
            <div className="cloud-wrap px-3 px-sm-5 mx-auto">
              <div className="cloud-img">
                <img
                  src="https://ags-images-bucket.s3.ap-south-1.amazonaws.com/images/cloud-computing.svg"
                  alt="cloud"
                />
              </div>
              <div className="cloud-text">
                <a href="#" onClick={e => e.preventDefault()} className="text-sky-blue text-decoration-none">
                  {loading ? 'Uploading...' : 'Browse'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="d-flex align-items-center mt-3 gap-2">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="text-muted">Uploading records, please wait...</span>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success mt-3" role="alert">
          <i className="bi bi-check-circle-fill me-2" />
          {success}
        </div>
      )}

      {skippedRows.length > 0 && (
        <div className="alert alert-warning mt-3" role="alert">
          <strong><i className="bi bi-exclamation-circle-fill me-2" />
            {skippedRows.length} row(s) were skipped — user_institute ID not found in database:
          </strong>
          <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>user_institute ID</th>
                  <th>Email</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {skippedRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{row.user_institute_id}</td>
                    <td>{row.to_email}</td>
                    <td>ID not found in user_institute table</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadReport;
